import React from 'react';
import ProductTable from '../ProductTable';
import HSNSummary from '../HSNSummary';

const ModernTemplate = ({
    invoiceSummary,
    products,
    sellerDetails,
    buyerDetails,
    amountInWords,
    gstType,
    isInterState,
    formatCurrency,
    invoiceNumber,
    invoiceDate,
    logo,
    shippingAddress,
    isSameAddress,
    showSignature,
    transportDetails
}) => {
    return (
        <div className="bg-white w-[794px] min-h-[1123px] mx-auto duration-200 font-sans">
            {/* Header Section */}
            <div className="bg-slate-900 text-white px-6 py-2 border-b-4 border-blue-900 relative">
                <div className="flex justify-between items-start">
                    <div>
                        <h1 className="text-4xl font-bold tracking-tight mb-1">
                            {gstType === 'Regular GST' ? 'TAX INVOICE' :
                                gstType === 'Composition GST' ? 'BILL OF SUPPLY' : 'INVOICE'}
                        </h1>

                        {/* Business Logo - Keep prominent */}
                        {logo && (
                            <div className="mt-2 bg-white p-2 rounded-lg inline-block w-auto">
                                <img
                                    src={logo}
                                    alt="Business Logo"
                                    className="object-contain"
                                    style={{ height: '80px', maxWidth: '180px', width: 'auto' }}
                                />
                            </div>
                        )}
                    </div>

                    <div className="text-right">
                        {/* Clean ERP Logo above Invoice Date - Wrapped in white for PDF visibility */}
                        <div className="mb-2 flex justify-end">
                            <div className="bg-white px-2 py-1 rounded shadow-sm">
                                <img src="/logo.png" alt="Clean ERP" className="h-4 w-auto object-contain" />
                            </div>
                        </div>
                        <div className="text-sm opacity-70 mb-1">Invoice Date</div>
                        <div className="font-semibold">
                            {new Date(invoiceDate).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </div>
                        <div className="text-sm opacity-70 mt-2 mb-1">Invoice No.</div>
                        <div className="text-blue-300 font-bold text-lg">{invoiceNumber || 'INV-001'}</div>
                    </div>
                </div>
            </div>

            <div className="px-6 pb-10 pt-2">
                {/* Addresses Grid */}
                <div className="mb-12">
                    {/* Top Row: Seller and Buyer */}
                    <div className="grid grid-cols-2 gap-12 mb-8">
                        {/* Left: Seller Info */}
                        <div>
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Seller Info</h3>
                            <div className="text-slate-900 font-bold text-xl mb-2">{sellerDetails.name}</div>
                            {sellerDetails.address && (
                                <div className="text-sm text-slate-500 mb-1 whitespace-pre-wrap">
                                    {sellerDetails.address}
                                </div>
                            )}
                            {sellerDetails.state && (
                                <div className="text-sm text-slate-500 font-mono bg-slate-100 px-2 py-1 rounded mb-1 block w-fit">
                                    State: {sellerDetails.state}
                                </div>
                            )}
                            {sellerDetails.gstNumber && (
                                <div className="text-sm text-slate-500 font-mono bg-slate-100 px-2 py-1 rounded block w-fit">
                                    GSTIN: {sellerDetails.gstNumber}
                                </div>
                            )}
                        </div>

                        {/* Right: Buyer Info */}
                        <div className="text-right">
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Buyer Info</h3>
                            <div className="text-slate-900 font-bold text-xl mb-2">{buyerDetails.name}</div>
                            {buyerDetails.address && (
                                <div className="text-sm text-slate-500 mb-1 whitespace-pre-wrap">
                                    {buyerDetails.address}
                                </div>
                            )}
                            {buyerDetails.state && (
                                <div className="text-sm text-slate-500 font-mono bg-slate-100 px-2 py-1 rounded mb-1 block w-fit ml-auto">
                                    State: {buyerDetails.state}
                                </div>
                            )}
                            {buyerDetails.gstNumber && buyerDetails.gstNumber !== 'NOT APPLICABLE' && (
                                <div className="text-sm text-slate-500 font-mono bg-slate-100 px-2 py-1 rounded block w-fit ml-auto">
                                    GSTIN: {buyerDetails.gstNumber}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Bottom Row: Billing Address (Left) and Shipping Address (Right) */}
                    <div className="grid grid-cols-2 gap-12">
                        {/* Billing Address - Left */}
                        <div>
                            <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Billing Address</h3>
                            <div className="text-sm text-slate-600 whitespace-pre-wrap">{buyerDetails.address}</div>
                        </div>

                        {/* Shipping Address - Right */}
                        <div className="text-right">
                            <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Shipping Address</h3>
                            <div className="text-sm text-slate-600 whitespace-pre-wrap">{isSameAddress ? buyerDetails.address : shippingAddress}</div>
                        </div>
                    </div>
                </div>

                {/* Transport Details Section */}
                {transportDetails && (transportDetails.transporterName || transportDetails.vehicleNo || transportDetails.ewayBillNo) && (
                    <div className="mb-8 border rounded-lg p-4 bg-slate-50 border-slate-200 text-sm">
                        <h3 className="text-xs font-bold text-slate-700 uppercase tracking-widest mb-3 border-b border-slate-200 pb-2">Transport Details</h3>
                        <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                            {transportDetails.transporterName && (
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Transporter:</span>
                                    <span className="font-semibold text-slate-900">{transportDetails.transporterName}</span>
                                </div>
                            )}
                            {transportDetails.transporterGst && (
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Transporter GSTIN:</span>
                                    <span className="font-semibold text-slate-900">{transportDetails.transporterGst}</span>
                                </div>
                            )}
                            {transportDetails.vehicleNo && (
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Vehicle No:</span>
                                    <span className="font-semibold text-slate-900">{transportDetails.vehicleNo}</span>
                                </div>
                            )}
                            {transportDetails.ewayBillNo && (
                                <div className="flex justify-between">
                                    <span className="text-slate-500">E-Way Bill No:</span>
                                    <span className="font-semibold text-slate-900">{transportDetails.ewayBillNo}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Product Table Structure Customization could happen here, keeping Component re-use for simplicity but wrapping it */}
                <div className="mb-12 border-b border-slate-200 pb-12">
                    <ProductTable
                        products={products}
                        isInterState={isInterState}
                        gstType={gstType}
                        isPreview={true}
                        stateTaxRate={9}
                    />
                    {gstType === 'Regular GST' && (
                        <div className="mt-8">
                            <HSNSummary
                                hsnSummary={invoiceSummary.hsnSummary || []}
                                isBillOfSupply={gstType !== 'Regular GST'}
                                isInterState={isInterState}
                            />
                        </div>
                    )}
                </div>

                {/* Footer Section */}
                <div className="flex flex-row justify-between items-start gap-12">
                    <div className="flex-1">
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Amount in Words</div>
                        <div className="text-slate-800 italic text-lg leading-relaxed">{amountInWords}</div>
                    </div>

                    <div className="w-1/3">
                        <div className="space-y-3">
                            <div className="flex justify-between text-slate-600">
                                <span>{gstType !== 'Regular GST' ? 'Sub Total' : 'Taxable Amount'}</span>
                                <span className="font-mono">{formatCurrency(invoiceSummary.totalTaxable)}</span>
                            </div>
                            {gstType === 'Regular GST' && (
                                <div className="flex justify-between text-slate-600">
                                    <span>Total Tax</span>
                                    <span className="font-mono">{formatCurrency((invoiceSummary.grandTotal - invoiceSummary.totalTaxable))}</span>
                                </div>
                            )}
                            <div className="h-px bg-slate-200 my-4"></div>
                            <div className="flex justify-between items-center">
                                <span className="text-slate-900 font-bold text-lg">Total Due</span>
                                <span className="text-blue-600 font-bold text-2xl font-mono">{formatCurrency(invoiceSummary.grandTotal)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {showSignature && (
                    <div className="mt-2 flex justify-end animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <div className="text-right">
                            <div className="text-xl font-bold text-slate-800 mb-1">{sellerDetails.name || 'Authorized Signatory'}</div>
                            <div className="h-6 mb-2 border-b border-slate-300 w-48 ml-auto"></div>
                            <div className="text-xs font-bold text-slate-500 uppercase tracking-tight">Authorized Signatory</div>
                        </div>
                    </div>
                )}

                <div className="mt-8 text-center text-slate-500 text-xs font-sans font-medium">
                    Thank you for your business
                </div>

            </div>
        </div>
    );
};

export default ModernTemplate;
